import React, { useState } from 'react';
import { toast } from '../utils/toast';
import { geminiApi } from '../services/geminiApi';
import { QuickStart } from '../components/QuickStart';
import { VoiceRecorder } from '../components/VoiceRecorder';
import { BetaBadge, BetaMessage } from '../components/BetaDisclaimer';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import DeploymentOptions from '../components/DeploymentOptions';

export function TrainingManualCreator() {
  const [contentInput, setContentInput] = useState('');
  const [audienceLevel, setAudienceLevel] = useState('beginner');
  const [manualType, setManualType] = useState('technical');
  const [format, setFormat] = useState('detailed');
  const [includeAssessments, setIncludeAssessments] = useState(true);
  const [generatedContent, setGeneratedContent] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const audienceLevelOptions = [
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'advanced', label: 'Advanced' },
    { value: 'expert', label: 'Expert' }
  ];

  const manualTypeOptions = [
    { value: 'technical', label: 'Technical Manual' },
    { value: 'process', label: 'Process Guide' },
    { value: 'software', label: 'Software Guide' },
    { value: 'onboarding', label: 'Onboarding Manual' },
    { value: 'safety', label: 'Safety Manual' }
  ];

  const formatOptions = [
    { value: 'detailed', label: 'Detailed Guide' },
    { value: 'quick', label: 'Quick Reference' },
    { value: 'stepbystep', label: 'Step-by-Step' },
    { value: 'interactive', label: 'Interactive Guide' }
  ];

  const quickStartGuides = [
    {
      title: "Content Structure",
      description: "Define the overall structure and flow of your training manual.",
      keywords: ["Sections", "Chapters", "Flow"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
      )
    },
    {
      title: "Learning Objectives",
      description: "Set clear, measurable learning objectives for your training.",
      keywords: ["Goals", "Outcomes", "Skills"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      title: "Assessment Design",
      description: "Create effective knowledge checks and practical exercises.",
      keywords: ["Tests", "Exercises", "Validation"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      )
    }
  ];

  const handleTranscriptionComplete = (transcribedText) => {
    setContentInput(transcribedText);
  };

  const generateContent = async () => {
    if (!contentInput.trim()) {
      toast.error('Please provide training content details');
      return;
    }

    setIsGenerating(true);
    try {
      const prompt = `Create a comprehensive training manual for: ${contentInput}
Audience Level: ${audienceLevel}
Manual Type: ${manualType}
Format: ${format}
Include Assessments: ${includeAssessments ? 'Yes' : 'No'}

Please include:
- Table of Contents
- Learning objectives for each section
- Detailed content with examples and illustrations placeholders
- Step-by-step instructions where applicable
- Knowledge check questions
- Practical exercises
- Summary and key takeaways
- Glossary of terms
${includeAssessments ? '- Assessment questions and answer key' : ''}

Format the response as a structured training manual with clear sections, headings, and bullet points.`;

      const response = await geminiApi.generateText(prompt);

      if (response.error) {
        throw new Error(response.error);
      } else {
        const generatedText = response.content || response.message;
        if (generatedText) {
          setGeneratedContent(generatedText);
          toast.success('Training manual generated successfully!');
        } else {
          throw new Error('No content received from the API');
        }
      }
    } catch (error) {
      console.error('Error generating training manual:', error);
      toast.error(error.message || 'Failed to generate training manual');
    } finally {
      setIsGenerating(false);
    }
  };

  const exportToPDF = () => {
    try {
      const doc = new jsPDF();
      
      // Add title
      doc.setFontSize(20);
      doc.text('Training Manual', 20, 20);
      
      // Add metadata
      doc.setFontSize(12);
      doc.text(`Audience Level: ${audienceLevel}`, 20, 30);
      doc.text(`Type: ${manualType}`, 20, 40);
      doc.text(`Format: ${format}`, 20, 50);
      
      // Add content
      doc.setFontSize(12);
      const splitContent = doc.splitTextToSize(generatedContent, 170);
      doc.text(splitContent, 20, 70);
      
      // Save the PDF
      doc.save('training-manual.pdf');
      toast.success('PDF exported successfully!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Failed to export PDF');
    }
  };

  const copyToClipboard = (content) => {
    navigator.clipboard.writeText(content);
    toast.success('Copied to clipboard!');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-white to-white relative">
      {/* Background effects */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(120,119,198,0.1),rgba(255,255,255,0)_43%),radial-gradient(circle_at_-20%_-20%,rgba(167,243,208,0.08),rgba(255,255,255,0)_35%)] pointer-events-none"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(147,197,253,0.08),rgba(255,255,255,0)_50%)] pointer-events-none"></div>
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:14px_14px] pointer-events-none"></div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Modern SaaS Header */}
        <div className="relative mb-8 p-8 rounded-3xl bg-gradient-to-r from-primary-900 via-primary-800 to-primary-900 shadow-2xl overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:24px_24px]"></div>
            <div className="absolute left-0 right-0 top-0 h-px bg-gradient-to-r from-transparent via-primary-400/20 to-transparent"></div>
            <div className="absolute left-0 right-0 bottom-0 h-px bg-gradient-to-r from-transparent via-primary-400/20 to-transparent"></div>
            <div className="absolute -left-48 -top-48 h-96 w-96">
              <div className="absolute inset-0 rotate-45 translate-x-1/2 translate-y-1/2 bg-gradient-conic from-primary-500/40 via-primary-300/10 to-primary-400/40 blur-xl opacity-30"></div>
            </div>
            <div className="absolute -right-48 -bottom-48 h-96 w-96">
              <div className="absolute inset-0 rotate-45 translate-x-1/2 translate-y-1/2 bg-gradient-conic from-primary-400/40 via-primary-300/10 to-primary-500/40 blur-xl opacity-30"></div>
            </div>
          </div>

          {/* Content */}
          <div className="relative">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div className="space-y-4">
                <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-primary-800/50 border border-primary-700/50 backdrop-blur-sm">
                  <div className="w-2 h-2 rounded-full bg-primary-400 animate-pulse"></div>
                  <span className="text-sm text-primary-100">AI-Powered Documentation</span>
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-white flex items-center">
                  Training Manual Creator
                  <BetaBadge className="ml-3" />
                </h1>
                <p className="text-lg text-primary-200 max-w-xl">
                  Create comprehensive training manuals with clear instructions and assessments
                </p>
                <BetaMessage />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
          <div className="p-6">
            <div className="space-y-8">
              {/* Input Section */}
              <div className="space-y-6">
                {/* Voice Recorder */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Record Training Content Details
                  </label>
                  <VoiceRecorder 
                    onTranscriptionComplete={handleTranscriptionComplete}
                    placeholder="Click to start recording training content details..."
                    className="mb-4"
                  />
                </div>

                {/* Content Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Training Content Overview
                  </label>
                  <textarea
                    value={contentInput}
                    onChange={(e) => setContentInput(e.target.value)}
                    rows="4"
                    placeholder="Describe your training content, objectives, and key topics..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Audience Level Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Audience Level
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {audienceLevelOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setAudienceLevel(option.value)}
                        className={`flex items-center justify-center p-3 rounded-lg border ${
                          audienceLevel === option.value
                            ? 'bg-primary-50 border-primary-500 text-primary-700'
                            : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Manual Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Manual Type
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {manualTypeOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setManualType(option.value)}
                        className={`flex items-center justify-center p-3 rounded-lg border ${
                          manualType === option.value
                            ? 'bg-primary-50 border-primary-500 text-primary-700'
                            : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Format Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Format Style
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {formatOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setFormat(option.value)}
                        className={`flex items-center justify-center p-3 rounded-lg border ${
                          format === option.value
                            ? 'bg-primary-50 border-primary-500 text-primary-700'
                            : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Include Assessments Toggle */}
                <div>
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={includeAssessments}
                      onChange={(e) => setIncludeAssessments(e.target.checked)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">Include Assessments</span>
                  </label>
                </div>

                <button
                  onClick={generateContent}
                  disabled={isGenerating}
                  className={`w-full px-6 py-3 rounded-lg font-medium text-white transition-all duration-200 ${
                    isGenerating
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-primary-600 hover:bg-primary-700'
                  }`}
                >
                  {isGenerating ? (
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Creating Training Manual...
                    </div>
                  ) : (
                    'Generate Training Manual'
                  )}
                </button>
              </div>

              {/* Generated Content Section */}
              {generatedContent ? (
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">Generated Training Manual</h3>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => copyToClipboard(generatedContent)}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Copy
                      </button>
                      <button
                        onClick={exportToPDF}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Export PDF
                      </button>
                    </div>
                  </div>
                  <div className="prose max-w-none">
                    <div className="whitespace-pre-wrap">{generatedContent}</div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No training manual generated yet</h3>
                  <p className="mt-1 text-sm text-gray-500">Start by describing your training content.</p>
                </div>
              )}
            </div>
          </div>
        </div>
        <QuickStart guides={quickStartGuides} />
        <DeploymentOptions appId="training-manual-creator" position="none" />
      </div>
    </div>
  );
}

export default TrainingManualCreator; 